/**
 * ADR-0214 Part 2 — the Subjects screen's tables, shared by the list, the
 * subject detail and the person page so a column reads the same everywhere.
 *
 * Plain counts in the default text style: no colour, icon or emphasis that
 * signals good or bad, and rows never ordered by a count (the hub orders by
 * name and time). Every time is absolute, in the workspace's zone.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { TrailState } from "../lib/activityTrail.js";
import {
  APPROX_TOKENS_TITLE,
  OUTCOME_LABEL,
  STATUS_LABEL,
  approxTokens,
  countOrDash,
  filedLabel,
  touchedLabel,
  unnamedLabel,
  when,
  whereLabel,
  type ActivityRow,
  type PersonRow,
  type SubjectRow,
} from "../lib/subjects.js";

export function DateRangeToolbar(props: { from: string; to: string; onChange: (r: { from: string; to: string }) => void; children?: ReactNode }) {
  return (
    <div className="toolbar">
      <div className="toolbarGroup">
        <label className="field" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="label">From</span>
          <input className="input" type="date" value={props.from} onChange={(e) => props.onChange({ from: e.target.value, to: props.to })} />
        </label>
        <label className="field" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="label">To</span>
          <input className="input" type="date" value={props.to} onChange={(e) => props.onChange({ from: props.from, to: e.target.value })} />
        </label>
        {props.children}
      </div>
    </div>
  );
}

/** A small titled table inside the house list card. */
export function Section(props: { title: string; children: ReactNode }) {
  return (
    <div className="card adminList">
      <div style={{ fontWeight: 700, padding: "10px 12px" }}>{props.title}</div>
      <div className="tableWrap tableWrapFrame">{props.children}</div>
    </div>
  );
}

export const SUBJECT_TABLE_HEADERS = ["Person", "Subject", "Opened", "Status", "Calls", "≈ Tokens", "Failed", "Knowledge", "Filed", "Asked before"] as const;

export function SubjectTable(props: { rows: SubjectRow[]; timeZone: string; withPerson: boolean; range: { from: string; to: string }; onward: TrailState }) {
  const headers = props.withPerson ? SUBJECT_TABLE_HEADERS : SUBJECT_TABLE_HEADERS.filter((h) => h !== "Person");
  return (
    <table className="table">
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h} title={h === "≈ Tokens" ? APPROX_TOKENS_TITLE : undefined}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.rows.map((r, i) => (
          <tr key={`${r.person_key ?? ""}-${r.subject_number ?? "none"}-${i}`}>
            {props.withPerson ? (
              <td>
                {r.person_key ? (
                  <Link to={`/app/subjects/people/${r.person_key}?from=${props.range.from}&to=${props.range.to}`} state={props.onward}>
                    {r.person ?? "—"}
                  </Link>
                ) : (
                  (r.person ?? "—")
                )}
              </td>
            ) : null}
            <td>
              {r.subject_number === null ? (
                <span className="muted">No subject</span>
              ) : (
                <>
                  <Link to={`/app/subjects/${r.subject_number}?from=${props.range.from}&to=${props.range.to}`} state={props.onward}>
                    {r.subject}
                  </Link>
                  {r.redacted ? (
                    <>
                      {" "}
                      <span className="pill pillNeutral">Redacted</span>
                    </>
                  ) : null}
                </>
              )}
            </td>
            <td>{when(r.opened_at, props.timeZone)}</td>
            <td>{r.status ? STATUS_LABEL[r.status] : "—"}</td>
            <td>{countOrDash(r.calls)}</td>
            <td>{approxTokens(r.chars)}</td>
            <td>{countOrDash(r.failed)}</td>
            <td>{r.knowledge_served === null ? "—" : r.knowledge_served === 0 ? "None found" : r.knowledge_served.toLocaleString()}</td>
            <td>{filedLabel(r.filed)}</td>
            <td>—</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const PERSON_TABLE_HEADERS = [
  "Person",
  "Last active",
  "Where",
  "Subjects",
  "Novel",
  "Without a subject",
  "Pages viewed",
  "Failed",
  "Blocked",
  "Concerns",
  "≈ Tokens",
] as const;

/** Door characters ÷ 4 plus the console AI's real tokens (§3A.2). */
function personTokens(p: Pick<PersonRow, "chars" | "ai_tokens">): string {
  return approxTokens(p.chars + p.ai_tokens * 4);
}

export function PersonTable(props: { rows: PersonRow[]; timeZone: string; range: { from: string; to: string }; onward: TrailState }) {
  return (
    <table className="table">
      <thead>
        <tr>
          {PERSON_TABLE_HEADERS.map((h) => (
            <th key={h} title={h === "≈ Tokens" ? "Approximate: the assistant's characters exchanged ÷ 4, plus the console AI's tokens." : undefined}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.rows.map((p) => (
          <tr key={p.person_key}>
            <td>
              <Link to={`/app/subjects/people/${p.person_key}?from=${props.range.from}&to=${props.range.to}`} state={props.onward}>
                {p.person}
              </Link>
              {!p.active ? <span className="muted"> (deactivated)</span> : null}
            </td>
            <td>{p.last_active ? when(p.last_active, props.timeZone) : "Never"}</td>
            <td>{whereLabel(p.where)}</td>
            <td>{countOrDash(p.subjects)}</td>
            <td>{countOrDash(p.novel)}</td>
            <td>{countOrDash(p.without_subject)}</td>
            <td>{countOrDash(p.pages_viewed)}</td>
            <td>{countOrDash(p.failed)}</td>
            <td>{countOrDash(p.blocked)}</td>
            <td>{countOrDash(p.concerns)}</td>
            <td>{personTokens(p)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const ACTIVITY_TABLE_HEADERS = ["When", "Where", "Action", "Outcome", "Touched"] as const;

export function ActivityTable(props: { rows: ActivityRow[]; timeZone: string }) {
  return (
    <table className="table">
      <thead>
        <tr>
          {ACTIVITY_TABLE_HEADERS.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.rows.map((a, i) => {
          const unnamed = unnamedLabel(a.touched_unnamed);
          return (
            <tr key={`${a.at}-${i}`}>
              <td>{when(a.at, props.timeZone)}</td>
              <td>{a.where}</td>
              <td className="mono">{a.action}</td>
              <td>{OUTCOME_LABEL[a.outcome]}</td>
              <td>
                {a.touched.length === 0 && !unnamed ? (
                  "—"
                ) : (
                  <>
                    {a.touched.map((t, j) => (
                      <div key={j}>{touchedLabel(t)}</div>
                    ))}
                    {unnamed ? <div className="muted">{unnamed}</div> : null}
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
